/**
 * Centralized authentication header utility
 *
 * Provides secure mock authentication for Playwright tests while ensuring
 * production safety through multiple security checks.
 *
 * Identity is conveyed only via Authorization: Bearer <token>; the backend
 * must verify the JWT and derive user id / profile from token claims.
 */

import { getEffectiveTenantHost } from '../config';
import { getAccessToken, getIdToken } from './tokenStorage';

/**
 * Check if we're running in a Playwright test environment
 * Multiple security checks ensure mock auth can't be enabled in production
 */
function isMockAuthEnabled(): boolean {
  // Security check 1: Must have Playwright test marker
  const hasPlaywrightMarker = typeof window !== 'undefined' &&
    (window as any).__PLAYWRIGHT_TEST__ === true;

  // Security check 2: Must not be in production environment
  const isNotProduction = process.env.NODE_ENV !== 'production';

  // Security check 3: Must be on localhost or test domain
  const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
     window.location.hostname === '127.0.0.1' ||
     window.location.hostname.startsWith('localhost:') ||
     window.location.hostname.includes('.test') ||
     window.location.hostname.includes('127.0.0.1'));

  // All three conditions must be true for mock auth to activate
  const mockAuthEnabled = hasPlaywrightMarker && isNotProduction && isLocalhost;

  // Log warning if suspicious conditions detected
  if (hasPlaywrightMarker && !isLocalhost) {
    console.warn('⚠️ Security: Playwright test marker detected but not on localhost. Mock auth disabled.');
  }

  return mockAuthEnabled;
}

/**
 * Tenant routing hint for local dev: when the API runs on localhost but the SPA
 * is served from a tenant subdomain, the backend uses trusted host headers to
 * resolve the tenant. Production tenant resolution uses infrastructure-injected
 * x-forwarded-host / host, not client identity headers.
 */
function getTenantRoutingHeaders(): Record<string, string> {
  return typeof window !== 'undefined'
    ? { 'X-Forwarded-Host': getEffectiveTenantHost() }
    : {};
}

/**
 * Get authentication headers for API calls
 * Returns mock headers when in test environment, real Auth0 headers otherwise
 */
const tokenSegmentCount = (token: string | null | undefined): number =>
  typeof token === 'string' && token.length > 0 ? token.split('.').length : 0;

const isSignedJwt = (token: string | null | undefined): token is string =>
  tokenSegmentCount(token) === 3;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) {
    return null;
  }
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function jwtAudiences(token: string): string[] {
  const aud = decodeJwtPayload(token)?.aud;
  if (typeof aud === 'string' && aud.length > 0) {
    return [aud];
  }
  if (Array.isArray(aud)) {
    return aud.filter((value): value is string => typeof value === 'string' && value.length > 0);
  }
  return [];
}

function createUnsignedMockJwt(sub: string): string {
  const encode = (value: object): string =>
    btoa(JSON.stringify(value))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  return `${encode({ alg: 'none', typ: 'JWT' })}.${encode({ sub })}.`;
}

/**
 * Bearer this API will accept. Verification requires `aud` to equal the OIDC
 * client id, which is the id token's audience. A signed access token is used
 * only when its `aud` includes that client id; otherwise the id token is sent.
 */
export function selectApiBearerToken(
  accessToken?: string | null,
  idToken?: string | null
): string | null {
  if (isSignedJwt(accessToken) && isSignedJwt(idToken)) {
    const clientAudiences = jwtAudiences(idToken);
    const accessAccepted =
      clientAudiences.length > 0 &&
      jwtAudiences(accessToken).some((aud) => clientAudiences.includes(aud));
    return accessAccepted ? accessToken : idToken;
  }
  if (isSignedJwt(accessToken)) return accessToken;
  if (isSignedJwt(idToken)) return idToken;
  return accessToken || idToken || null;
}

export function getAuthHeaders(): Record<string, string> {
  const tenantRouting = getTenantRoutingHeaders();

  const useMockAuth = isMockAuthEnabled();

  if (useMockAuth) {
    const accessToken = getAccessToken();
    const bearer = accessToken || createUnsignedMockJwt('mock-user-id');
    return {
      'Authorization': `Bearer ${bearer}`,
      'Content-Type': 'application/json',
      ...tenantRouting,
    };
  }

  const accessToken = getAccessToken();
  const idToken = getIdToken();
  const apiToken = selectApiBearerToken(accessToken, idToken);

  if (apiToken) {
    return {
      'Authorization': `Bearer ${apiToken}`,
      'Content-Type': 'application/json',
      ...tenantRouting,
    };
  }

  return {
    'Content-Type': 'application/json',
    ...tenantRouting,
  };
}

/**
 * Check if mock authentication is currently enabled
 * Useful for conditional logic in components
 */
export function isMockAuthActive(): boolean {
  return isMockAuthEnabled();
}
